// Admin store module - handles admin panel data, user management, and dashboard stats
const API_BASE = process.env.VUE_APP_API_BASE;
const API_USUARIOS = `${API_BASE}/ajax/usuarios.php`;

export default {
  namespaced: true,

  state: {
    clients: [],
    admins: [],
    loading: false,
    error: null,
  },

  getters: {
    getAllClients: (state) => state.clients,
    getAllAdmins: (state) => state.admins,
    getClientById: (state) => (id) => {
      return state.clients.find((client) => client.id_usuario === id)
    },
    getAdminById: (state) => (id) => {
      return state.admins.find((admin) => admin.id_usuario === id)
    },
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
  },

  mutations: {
    SET_CLIENTS(state, clients) {
      state.clients = clients
    },
    SET_ADMINS(state, admins) {
      state.admins = admins
    },
    ADD_CLIENT(state, client) {
      if (!client) return

      // Aseguramos que los arrays existan
      if (!state.clients) state.clients = []
      if (!state.admins) state.admins = []

      // Si es admin, agregar al arreglo de admins
      if (client.id_role === 1) {
        state.admins.push(client)
      } else {
        state.clients.push(client)
      }
    },
    UPDATE_CLIENT(state, updatedClient) {
      const index = state.clients.findIndex((c) => c.id_usuario === updatedClient.id_usuario)
      if (index !== -1) {
        state.clients.splice(index, 1, updatedClient)
      }
    },
    DELETE_CLIENT(state, clientId) {
      state.clients = state.clients.filter((c) => c.id_usuario !== clientId)
    },
    UPDATE_ADMIN(state, updatedAdmin) {
      if (!updatedAdmin) return
      if (!Array.isArray(state.admins)) state.admins = []

      const index = state.admins.findIndex(
        (a) => a.id_usuario === updatedAdmin.id_usuario
      )

      if (index !== -1) {
        state.admins.splice(index, 1, updatedAdmin)
      } else {
        state.admins.push(updatedAdmin)
      }
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
  },

  actions: {
    async fetchClients({ commit }) {
      commit("SET_LOADING", true)
      commit("SET_ERROR", null)
      try {
        const response = await fetch(`${API_USUARIOS}?action=getAll`)
        const data = await response.json()

        if (data.success) {
          commit("SET_CLIENTS", data.usuarios || [])
          return { success: true, count: data.usuarios?.length || 0 }
        } else {
          commit("SET_ERROR", data.message || "Error al cargar usuarios")
          return { success: false, message: data.message }
        }
      } catch (error) {
        console.error("Error fetching clients:", error)
        commit("SET_ERROR", "Error de conexión al cargar usuarios")
      } finally {
        commit("SET_LOADING", false)
      }
    },

    async fetchAdmins({ commit }) {
      commit("SET_LOADING", true)
      commit("SET_ERROR", null)
      try {
        const response = await fetch(`${API_USUARIOS}?action=getAllAdmins`)
        const data = await response.json()

        if (data.success) {
          commit("SET_ADMINS", data.admins || [])
          return { success: true, count: data.admins?.length || 0 }
        } else {
          commit("SET_ERROR", data.message || "Error al cargar administradores")
          return { success: false, message: data.message }
        }
      } catch (error) {
        console.error("Error fetching admins:", error)
        commit("SET_ERROR", "Error de conexión al cargar administradores")
        return { success: false, message: "Error de conexión" }
      } finally {
        commit("SET_LOADING", false)
      }
    },

    async addClient({ commit }, clientData) {
      commit("SET_LOADING", true)
      commit("SET_ERROR", null)
      try {
        const response = await fetch(`${API_USUARIOS}?action=add`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(clientData),
        })
        const data = await response.json()

        if (data.success) {
          commit("ADD_CLIENT", data.client)
          return { success: true }
        } else {
          commit("SET_ERROR", data.message || "Error al agregar cliente")
          return { success: false, message: data.message }
        }
      } catch (error) {
        console.error("Error adding client:", error)
        commit("SET_ERROR", "Error de conexión al agregar cliente")
        return { success: false, message: "Error de conexión" }
      } finally {
        commit("SET_LOADING", false)
      }
    },

    async updateClient({ commit }, clientData) {
      commit("SET_LOADING", true)
      commit("SET_ERROR", null)
      try {
        const response = await fetch(`${API_USUARIOS}?action=update`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(clientData),
        })
        const data = await response.json()

        if (data.success) {
          commit("UPDATE_CLIENT", data.client)
          return { success: true }
        } else {
          commit("SET_ERROR", data.message || "Error al actualizar cliente")
          return { success: false, message: data.message }
        }
      } catch (error) {
        console.error("Error updating client:", error)
        commit("SET_ERROR", "Error de conexión al actualizar cliente")
        return { success: false, message: "Error de conexión" }
      } finally {
        commit("SET_LOADING", false)
      }
    },

    async passClient({ commit }, clientData) {
      commit("SET_LOADING", true)
      commit("SET_ERROR", null)
      try {
        const response = await fetch(`${API_USUARIOS}?action=updatePass`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(clientData),
        })
        const data = await response.json()

        if (data.success) {
          commit("UPDATE_CLIENT", data.client)
          return { success: true }
        } else {
          commit("SET_ERROR", data.message || "Error al actualizar cliente")
          return { success: false, message: data.message }
        }
      } catch (error) {
        console.error("Error updating client:", error)
        commit("SET_ERROR", "Error de conexión al actualizar cliente")
        return { success: false, message: "Error de conexión" }
      } finally {
        commit("SET_LOADING", false)
      }
    },

    async updateAdmin({ commit }, adminData) {
      commit("SET_LOADING", true)
      commit("SET_ERROR", null)
      try {
        const response = await fetch(`${API_USUARIOS}?action=update`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(adminData),
        })
        const data = await response.json()

        if (data.success) {
          commit("UPDATE_ADMIN", data.client)
          return { success: true }
        } else {
          commit("SET_ERROR", data.message || "Error al actualizar administrador")
          return { success: false, message: data.message }
        }
      } catch (error) {
        console.error("Error updating admin:", error)
        commit("SET_ERROR", "Error de conexión al actualizar administrador")
        return { success: false, message: "Error de conexión" }
      } finally {
        commit("SET_LOADING", false)
      }
    },

    async passAdmin({ commit }, adminData) {
      commit("SET_LOADING", true)
      commit("SET_ERROR", null)
      try {
        const response = await fetch(`${API_USUARIOS}?action=updatePass`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(adminData),
        })
        const data = await response.json()

        if (data.success) {
          commit("UPDATE_ADMIN", data.client)
          return { success: true }
        } else {
          commit("SET_ERROR", data.message || "Error al actualizar administrador")
          return { success: false, message: data.message }
        }
      } catch (error) {
        console.error("Error updating admin password:", error)
        commit("SET_ERROR", "Error de conexión al actualizar administrador")
        return { success: false, message: "Error de conexión" }
      } finally {
        commit("SET_LOADING", false)
      }
    },

    // Cambiar vigencia del usuario
    async toggleClientVigencia({ dispatch }, { id_usuario, vigente }) {
      try {
        const response = await fetch(`${API_USUARIOS}?action=setVigente`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_usuario,
            vigente: Number(vigente),
          }),
        })
        const data = await response.json()

        if (data.success) {
          await dispatch("fetchClients")
          return { success: true }
        } else {
          return { success: false, message: data.message }
        }
      } catch (error) {
        console.error("Error changing vigencia:", error)
        return { success: false, message: "Error de conexión" }
      }
    },

    async deleteClient({ commit }, clientId) {
      commit("SET_LOADING", true)
      commit("SET_ERROR", null)
      try {
        const response = await fetch(`${API_USUARIOS}?action=delete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id_usuario: clientId }),
        })
        const data = await response.json()

        if (data.success) {
          commit("DELETE_CLIENT", clientId)
          return { success: true }
        } else {
          commit("SET_ERROR", data.message || "Error al eliminar cliente")
          return { success: false, message: data.message }
        }
      } catch (error) {
        console.error("Error deleting client:", error)
        commit("SET_ERROR", "Error de conexión al eliminar cliente")
        return { success: false, message: "Error de conexión" }
      } finally {
        commit("SET_LOADING", false)
      }
    },
  },
}
